import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { Spinner } from "../components/Spinner";

/**
 * Giriş ekranı.
 *
 * ESKİ SİSTEMDEN FARKLAR:
 * · G-24 açık yönlendirme: eski `index.php?continue=<url>` parametresi hiç
 *   doğrulanmıyordu. Burada hedef adres URL'de taşınmaz (router state), ayrıca
 *   yalnızca uygulama içi yol olduğu doğrulandıktan sonra kullanılır.
 * · G-28 e-posta numaralandırma: "kullanıcı yok" ile "şifre yanlış" ayrımı
 *   yapılmaz; ikisi de aynı mesajı döner.
 * · G-27 `outofwork`: rolü olmayan / devre dışı hesap oturum açamaz ve bunu
 *   açıkça görür (eskiden giriş yapıp boş ekranlarla karşılaşıyordu).
 */

/** Yalnızca uygulama içi yollara dönülür. Protokol/host içeren her şey reddedilir. */
function safeRedirect(raw: unknown): string {
  if (typeof raw !== "string") return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//")) return "/";
  if (raw.includes("://") || raw.includes("\\")) return "/";
  if (raw.startsWith("/login")) return "/";
  return raw;
}

/** Firebase Auth hata kodlarının Türkçe karşılığı. */
function authErrorMessage(code: string): string {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-email":
      // Numaralandırmayı önlemek için üçü de aynı mesaj.
      return "E-posta veya şifre hatalı.";
    case "auth/user-disabled":
      return "Bu hesap devre dışı bırakılmış. İş sahibine başvurun.";
    case "auth/too-many-requests":
      return "Çok fazla hatalı deneme yapıldı. Bir süre bekleyip tekrar deneyin.";
    case "auth/network-request-failed":
      return "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.";
    case "auth/operation-not-allowed":
      return "E-posta ile giriş kapalı. Yöneticinize bildirin.";
    default:
      return `Giriş yapılamadı (${code}).`;
  }
}

export function Login() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const target = safeRedirect((location.state as { from?: unknown } | null)?.from);

  useEffect(() => {
    if (!loading && session) navigate(target, { replace: true });
  }, [loading, session, navigate, target]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);

      // Rol/aktiflik claim'i sunucuda atanır. Yoksa oturum açık bırakılmaz.
      const { claims } = await cred.user.getIdTokenResult(true);
      const role = claims.role;
      const active = claims.active === true;
      if (typeof role !== "string" || !role || !active) {
        await signOut(auth);
        setError(
          "Hesabınız henüz etkinleştirilmemiş veya yetkiniz kaldırılmış. İş sahibine başvurun.",
        );
        return;
      }
      // Yönlendirmeyi useEffect yapar (oturum context'e düşünce).
    } catch (e) {
      const code =
        typeof e === "object" && e !== null && typeof (e as { code?: unknown }).code === "string"
          ? (e as { code: string }).code
          : "auth/unknown";
      console.error("Giriş hatası", e);
      setError(authErrorMessage(code));
    } finally {
      setBusy(false);
    }
  };

  const onResetPassword = async () => {
    setError(null);
    setNotice(null);
    const addr = email.trim();
    if (!addr) {
      setError("Şifre sıfırlama bağlantısı için önce e-posta adresinizi yazın.");
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, addr);
    } catch (e) {
      // Hesabın var olup olmadığını SIZDIRMAYIZ; ağ hatası dışındakiler yutulur.
      const code =
        typeof e === "object" && e !== null && typeof (e as { code?: unknown }).code === "string"
          ? (e as { code: string }).code
          : "";
      if (code === "auth/network-request-failed" || code === "auth/too-many-requests") {
        setError(authErrorMessage(code));
        setBusy(false);
        return;
      }
      console.warn("Şifre sıfırlama", e);
    }
    setNotice(
      "Bu adres kayıtlıysa şifre sıfırlama bağlantısı gönderildi. Gelen kutunuzu kontrol edin.",
    );
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen flex-col justify-center bg-primary-700 px-4 py-12 sm:px-6">
      <div className="mx-auto w-full max-w-sm">
        <div className="text-center">
          <span className="text-2xl font-bold tracking-wide text-white">
            BALDER <span className="font-normal text-primary-200">ERP</span>
          </span>
          <p className="mt-1 text-sm text-primary-200">Sipariş ve üretim paneli</p>
        </div>

        <div className="mt-8 rounded-lg bg-white p-6 shadow-xl">
          <h1 className="text-base font-semibold text-gray-900">Giriş</h1>

          <form className="mt-4 space-y-4" onSubmit={onSubmit} noValidate>
            <div>
              <label htmlFor="email" className="label">
                E-posta
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                className="input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Şifre
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </div>

            {error ? (
              <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </p>
            ) : null}

            {notice ? (
              <p role="status" className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
                {notice}
              </p>
            ) : null}

            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? <Spinner size="sm" label="Giriş yapılıyor" /> : null}
              {busy ? "Giriş yapılıyor…" : "Giriş yap"}
            </button>
          </form>

          <button
            type="button"
            className="mt-3 text-sm text-primary-700 hover:underline"
            onClick={() => void onResetPassword()}
            disabled={busy}
          >
            Şifremi unuttum
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-primary-200">
          Hesaplar iş sahibi tarafından açılır. Kendi kendine kayıt yoktur.
        </p>
      </div>
    </div>
  );
}

export default Login;
